import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material'
import { PersonAdd, Phone, VerifiedUser, CheckCircle } from '@mui/icons-material'
import {
  useStartPhoneVerification,
  useVerifyPhone,
  useCompleteRegistration,
} from '../hooks/useAuth'
import { SocialLoginButtons } from '../components/SocialLoginButtons'
import { authApi } from '@api/endpoints/auth'

// Step 1: Phone Number Schema
const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .regex(/^\+[1-9]\d{1,14}$/, 'Phone number must be in E.164 format (e.g., +1234567890)'),
  shareCode: z.string().min(1, 'Referral code is required'),
})

// Step 2: Verification Code Schema
const verificationSchema = z.object({
  code: z.string().length(7, 'Verification code must be 7 digits'),
})

// Step 3: Registration Details Schema
const registrationSchema = z
  .object({
    email: z.string().email('Invalid email address'),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Password must contain uppercase letter')
      .regex(/[a-z]/, 'Password must contain lowercase letter')
      .regex(/[0-9]/, 'Password must contain a number'),
    confirmPassword: z.string(),
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type PhoneFormData = z.infer<typeof phoneSchema>
type VerificationFormData = z.infer<typeof verificationSchema>
type RegistrationFormData = z.infer<typeof registrationSchema>

const steps = ['Verify Phone', 'Enter Code', 'Create Account']

export default function RegisterPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [activeStep, setActiveStep] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Step data
  const [sessionId, setSessionId] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [verifiedPhoneToken, setVerifiedPhoneToken] = useState<string>('')

  // Mutations
  const startPhoneVerification = useStartPhoneVerification()
  const verifyPhone = useVerifyPhone()
  const completeRegistration = useCompleteRegistration()

  // Get referral code from localStorage or URL params
  const getReferralCode = () => {
    const storedCode = localStorage.getItem('referralCode')
    const urlCode = searchParams.get('code')
    const code = storedCode || urlCode || ''
    // Strip the U prefix if present (backend expects code without prefix)
    return code.toUpperCase().startsWith('U') ? code.substring(1) : code
  }

  // Forms
  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phoneNumber: '',
      shareCode: getReferralCode(),
    },
  })

  // Sync referral code from URL params to form
  useEffect(() => {
    const urlCode = searchParams.get('code')
    if (urlCode) {
      // Strip the U prefix if present (backend expects code without prefix)
      const cleanCode = urlCode.toUpperCase().startsWith('U')
        ? urlCode.substring(1)
        : urlCode
      phoneForm.setValue('shareCode', cleanCode)
    }
  }, [searchParams])

  // Clear referral code from localStorage after form is initialized
  useEffect(() => {
    const storedCode = localStorage.getItem('referralCode')
    if (storedCode) {
      localStorage.removeItem('referralCode')
    }
  }, [])

  // Format phone number for display (xxx) xxx-xxxx
  const formatPhoneDisplay = (value: string) => {
    const cleaned = value.replace(/\D/g, '')
    if (cleaned.length === 0) return ''

    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/)
    if (!match) return value

    let formatted = ''
    if (match[1]) {
      formatted = `(${match[1]}`
      if (match[1].length === 3) {
        formatted += ')'
        if (match[2]) formatted += ` ${match[2]}`
        if (match[2].length === 3 && match[3]) formatted += `-${match[3]}`
      }
    }

    return formatted
  }

  // Get display value from form
  const getDisplayValue = () => {
    const value = phoneForm.watch('phoneNumber') || ''
    // If it's in E.164 format, strip the +1 and format it
    if (value.startsWith('+1')) {
      return formatPhoneDisplay(value.substring(2))
    }
    // Otherwise format whatever is there
    return formatPhoneDisplay(value)
  }

  // Convert display format to E.164 format
  const convertToE164 = (displayValue: string) => {
    const cleaned = displayValue.replace(/\D/g, '')
    // Add +1 prefix for US numbers
    return cleaned.length === 10 ? `+1${cleaned}` : ''
  }

  // Handle phone number input
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const cleaned = value.replace(/\D/g, '')

    // Limit to 10 digits for US numbers
    if (cleaned.length <= 10) {
      // Store in E.164 format if we have digits
      const e164 = cleaned.length === 10 ? convertToE164(cleaned) : cleaned
      phoneForm.setValue('phoneNumber', e164, { shouldValidate: cleaned.length === 10 })
    }
  }

  const verificationForm = useForm<VerificationFormData>({
    resolver: zodResolver(verificationSchema),
  })

  const registrationForm = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
  })

  // Step 1: Submit phone number
  const handlePhoneSubmit = async (data: PhoneFormData) => {
    setError(null)

    // Ensure we have a valid phone number
    if (!data.phoneNumber || !data.phoneNumber.startsWith('+1')) {
      setError('Please enter a valid 10-digit phone number')
      return
    }

    try {
      const response = await startPhoneVerification.mutateAsync({
        phoneNumber: data.phoneNumber,
        shareCode: data.shareCode,
      })

      // Log the full response for debugging
      console.log('[Registration] Start Phone Verification Response:', JSON.stringify(response, null, 2))

      // Check if the operation was successful
      if (!response.success) {
        // Handle error response (e.g., invalid share code)
        console.error('[Registration] Operation failed:', response.message, 'Error Code:', response.errorCode)
        setError(response.message || 'Failed to send verification code')
        return
      }

      console.log('[Registration] Phone verification started successfully')
      setSessionId(response.sessionId)
      setPhoneNumber(data.phoneNumber)
      setActiveStep(1)
    } catch (err: any) {
      console.error('[Registration] Exception caught:', err)
      console.error('[Registration] Error response:', err.response)
      const errorMessage = err.response?.data?.message || 'Failed to send verification code'
      setError(errorMessage)
    }
  }

  // Step 2: Submit verification code
  const handleVerificationSubmit = async (data: VerificationFormData) => {
    setError(null)

    try {
      const response = await verifyPhone.mutateAsync({
        phoneNumber,
        code: data.code,
        sessionId,
      })

      // Log the full response for debugging
      console.log('[Registration] Verify Phone Response:', JSON.stringify(response, null, 2))

      // Check if the operation was successful
      if (!response.success) {
        console.error('[Registration] Verification failed:', response.message, 'Error Code:', response.errorCode)
        setError(response.message || 'Invalid verification code')
        return
      }

      console.log('[Registration] Phone verified successfully')
      setVerifiedPhoneToken(response.verifiedPhoneToken)
      setActiveStep(2)
    } catch (err: any) {
      console.error('[Registration] Exception caught:', err)
      console.error('[Registration] Error response:', err.response)
      const errorMessage = err.response?.data?.message || 'Invalid verification code'
      setError(errorMessage)
    }
  }

  // Step 3: Complete registration
  const handleRegistrationSubmit = async (data: RegistrationFormData) => {
    setError(null)

    try {
      const response = await completeRegistration.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        verifiedPhoneToken,
      })

      // Log the full response for debugging
      console.log('[Registration] Complete Registration Response:', JSON.stringify(response, null, 2))

      // Check if the operation was successful
      if (!response.success) {
        console.error('[Registration] Registration failed:', response.message, 'Error Code:', response.errorCode)
        setError(response.message || 'Registration failed. Please try again.')

        // If token expired, allow user to go back and reverify
        const message = response.message || ''
        if (message.toLowerCase().includes('expired') || message.toLowerCase().includes('invalid')) {
          setTimeout(() => {
            setError(message + ' Please verify your phone number again.')
          }, 100)
        }
        return
      }

      console.log('[Registration] Registration completed successfully!')
      setSuccess(true)
      setTimeout(() => navigate('/adventures'), 2000)
    } catch (err: any) {
      console.error('[Registration] Exception caught:', err)
      console.error('[Registration] Error response:', err.response)
      const errorMessage = err.response?.data?.message || 'Registration failed. Please try again.'
      setError(errorMessage)

      // If token expired, allow user to go back and reverify
      if (errorMessage.toLowerCase().includes('expired') || errorMessage.toLowerCase().includes('invalid')) {
        setTimeout(() => {
          setError(errorMessage + ' Please verify your phone number again.')
        }, 100)
      }
    }
  }

  // Resend code
  const handleResendCode = async () => {
    setError(null)

    try {
      const response = await startPhoneVerification.mutateAsync({
        phoneNumber,
        shareCode: phoneForm.getValues('shareCode'),
      })

      // Check if the operation was successful
      if (!response.success) {
        setError(response.message || 'Failed to resend verification code')
        return
      }

      setSessionId(response.sessionId)
      setError(null)
      // Show success message
      setTimeout(() => setError('Verification code resent!'), 100)
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to resend verification code'
      setError(errorMessage)
    }
  }

  // Social login handler
  const handleSocialLogin = (provider: string) => {
    // Redirect to OAuth provider
    const returnUrl = window.location.origin + '/auth/callback'
    authApi.initiateExternalLogin(provider, returnUrl)
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <Card sx={{ maxWidth: 450, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Registration Successful!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Redirecting to home page...
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        py: 4,
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Closed Testing Banner */}
          <Alert severity="info" sx={{ mb: 3 }}>
            This site is currently in <strong>closed testing</strong>. A share code is required to create an account.
          </Alert>

          <Typography variant="h4" gutterBottom align="center" sx={{ mb: 3 }}>
            Create Account
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Step 1: Phone Number */}
          {activeStep === 0 && (
            <form onSubmit={phoneForm.handleSubmit(handlePhoneSubmit)}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your phone number to receive a verification code.
              </Typography>

              <TextField
                label="Phone Number"
                fullWidth
                margin="normal"
                placeholder="(555) 123-4567"
                error={!!phoneForm.formState.errors.phoneNumber}
                helperText={
                  phoneForm.formState.errors.phoneNumber?.message ||
                  'Enter your 10-digit US phone number'
                }
                autoComplete="tel"
                onChange={handlePhoneNumberChange}
                value={getDisplayValue()}
                InputProps={{
                  startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <TextField
                {...phoneForm.register('shareCode')}
                label="Referral Code"
                fullWidth
                margin="normal"
                required
                error={!!phoneForm.formState.errors.shareCode}
                helperText={
                  phoneForm.formState.errors.shareCode?.message ||
                  'A referral code is required to create an account'
                }
                autoComplete="off"
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={startPhoneVerification.isPending}
                startIcon={
                  startPhoneVerification.isPending ? <CircularProgress size={20} /> : <Phone />
                }
                sx={{ mt: 2, mb: 2 }}
              >
                {startPhoneVerification.isPending ? 'Sending Code...' : 'Send Verification Code'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2">
                  Already have an account?{' '}
                  <Link component={RouterLink} to="/login">
                    Login here
                  </Link>
                </Typography>
              </Box>
            </form>
          )}

          {/* Step 2: Verification Code */}
          {activeStep === 1 && (
            <form onSubmit={verificationForm.handleSubmit(handleVerificationSubmit)}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A 7-digit verification code has been sent to <strong>{phoneNumber}</strong>. Enter
                the code below.
              </Typography>

              <TextField
                {...verificationForm.register('code')}
                label="Verification Code"
                fullWidth
                margin="normal"
                placeholder="1234567"
                error={!!verificationForm.formState.errors.code}
                helperText={verificationForm.formState.errors.code?.message}
                autoComplete="one-time-code"
                InputProps={{
                  startAdornment: <VerifiedUser sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={verifyPhone.isPending}
                startIcon={
                  verifyPhone.isPending ? <CircularProgress size={20} /> : <VerifiedUser />
                }
                sx={{ mt: 2, mb: 1 }}
              >
                {verifyPhone.isPending ? 'Verifying...' : 'Verify Code'}
              </Button>

              <Button
                onClick={handleResendCode}
                variant="text"
                fullWidth
                size="small"
                disabled={startPhoneVerification.isPending}
                sx={{ mb: 2 }}
              >
                Resend Code
              </Button>

              <Button
                onClick={() => {
                  setActiveStep(0)
                  phoneForm.setValue('phoneNumber', '')
                }}
                variant="outlined"
                fullWidth
                size="small"
              >
                Change Phone Number
              </Button>
            </form>
          )}

          {/* Social Login - Show after phone verification (Step 2) */}
          {activeStep === 1 && (
            <Box sx={{ mt: 2 }}>
              <SocialLoginButtons
                onProviderClick={handleSocialLogin}
                isLoading={verifyPhone.isPending || startPhoneVerification.isPending}
              />
            </Box>
          )}

          {/* Step 3: Registration Details */}
          {activeStep === 2 && (
            <form onSubmit={registrationForm.handleSubmit(handleRegistrationSubmit)}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Phone verified! Complete your registration below.
              </Typography>

              <TextField
                {...registrationForm.register('firstName')}
                label="First Name"
                fullWidth
                margin="normal"
                error={!!registrationForm.formState.errors.firstName}
                helperText={registrationForm.formState.errors.firstName?.message}
                autoComplete="given-name"
              />

              <TextField
                {...registrationForm.register('lastName')}
                label="Last Name"
                fullWidth
                margin="normal"
                error={!!registrationForm.formState.errors.lastName}
                helperText={registrationForm.formState.errors.lastName?.message}
                autoComplete="family-name"
              />

              <TextField
                {...registrationForm.register('email')}
                label="Email"
                type="email"
                fullWidth
                margin="normal"
                error={!!registrationForm.formState.errors.email}
                helperText={registrationForm.formState.errors.email?.message}
                autoComplete="email"
              />

              <TextField
                {...registrationForm.register('password')}
                label="Password"
                type="password"
                fullWidth
                margin="normal"
                error={!!registrationForm.formState.errors.password}
                helperText={registrationForm.formState.errors.password?.message}
                autoComplete="new-password"
              />

              <TextField
                {...registrationForm.register('confirmPassword')}
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                error={!!registrationForm.formState.errors.confirmPassword}
                helperText={registrationForm.formState.errors.confirmPassword?.message}
                autoComplete="new-password"
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={completeRegistration.isPending}
                startIcon={
                  completeRegistration.isPending ? <CircularProgress size={20} /> : <PersonAdd />
                }
                sx={{ mt: 2, mb: 1 }}
              >
                {completeRegistration.isPending ? 'Creating Account...' : 'Complete Registration'}
              </Button>

              <Button
                onClick={() => {
                  setActiveStep(0)
                  setError(null)
                  setSessionId('')
                  setVerifiedPhoneToken('')
                  phoneForm.setValue('phoneNumber', '')
                  verificationForm.reset()
                }}
                variant="outlined"
                fullWidth
                size="small"
              >
                Reverify Phone Number
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
